import { Referral, User } from 'types';
import { UnifiedReferralModel } from '../models/UnifiedReferralModel';

export interface AdapterContext {
    caseloadStatus?: string;
    profileStage?: string;
    user: User;
    direction: 'incoming' | 'outgoing';
    caseloadReferralIDs: number[];
    caseloadUserID?: number;
}

export class CaseManagementReferralAdapter {
    private static determineStatusText(
        referral: Referral,
        context: AdapterContext
    ): string {
        const { 
            caseloadStatus, 
            profileStage, 
            direction,
            user,
            caseloadReferralIDs,
            caseloadUserID
        } = context;

        const { 
            is_accepted, 
            is_rejected, 
            is_re_referred,
            referee,
            organization_id: orgID
        } = referral;

        if (direction === 'incoming') {
            if (referee?.id === user.id) {
                if (is_accepted && caseloadStatus === 'active' && caseloadUserID === user.id) {
                    return 'Accepted';
                }
                if (is_rejected && caseloadStatus === 'cancelled' && caseloadUserID === user.id) {
                    return 'Rejected';
                }
                if (is_accepted) return 'Accepted';
                if (is_re_referred && !is_rejected) return 'Expired';
                if (profileStage === 'EXITED') return 'Member Unenrolled';
                if (!is_accepted && !is_re_referred && !is_rejected) {
                    return caseloadStatus === 'active' ? 'Active Caseload' : 'Pending';
                }
            } else if (orgID === user.partner?.id) {
                if (is_accepted && caseloadStatus === 'active' && caseloadUserID === user.id) {
                    return 'Accepted';
                }
                if (is_rejected) return 'Rejected';
                if (!is_accepted && is_re_referred && !is_rejected) return 'Expired';
                if (caseloadStatus === 'cancelled' && caseloadUserID === user.id) {
                    return 'Rejected by You';
                }
                if (is_accepted && ((!is_re_referred && caseloadStatus === 'pending') ||
                    (is_re_referred && caseloadStatus === 'active')) &&
                    caseloadUserID !== user.id) {
                    return 'Accepted';
                }
                if (is_accepted && !is_re_referred && caseloadStatus !== 'active' &&
                    caseloadUserID === user.id) {
                    return 'Org Accepted';
                }
                if (profileStage === 'EXITED') return 'Member Unenrolled';
                if (!is_accepted && !is_re_referred && !is_rejected) {
                    return caseloadStatus === 'active' ? 'Active Caseload' : 'Pending';
                }
            } else {
                if (is_accepted) return 'Accepted';
                if (is_re_referred) return 'Expired';
                if (is_rejected) return 'Rejected';
                if (profileStage === 'EXITED') return 'Member Unenrolled';
                return 'Pending';
            }
        }

        // Outgoing
        if (is_accepted && !is_re_referred && caseloadStatus === 'pending' && !referee?.id) {
            return 'Org Accepted';
        }
        if (is_re_referred) return 'Expired';
        if (is_rejected) return 'Rejected';
        if (is_accepted && caseloadStatus === 'cancelled' && user.id === caseloadUserID &&
            caseloadReferralIDs.includes(referral.id)) {
            return 'Rejected by You';
        }
        if (!is_accepted && !is_re_referred && !is_rejected) {
            return caseloadStatus === 'active' ? 'Active Caseload' : 'Pending';
        }
        if (is_accepted) return 'Accepted';
        if (profileStage === 'EXITED' || profileStage === 'UNSUCCESSFUL_PENDING') {
            return 'Member Unenrolled';
        }

        return 'Pending';
    }

    private static formatParticipantName(participant: { id: number; name: string }, userId: number): string {
        return participant.id === userId ? 'You' : participant.name;
    }

    static adapt(referral: Referral, context: AdapterContext): UnifiedReferralModel {
        const statusText = this.determineStatusText(referral, context);
        const { user, caseloadStatus, profileStage } = context;
        const { is_accepted, is_rejected, is_re_referred, organization_id: orgID, referrer, organization } = referral;

        const isActiveCaseload = !is_accepted && !is_re_referred && !is_rejected && 
            caseloadStatus === 'active' && context.direction === 'incoming' && 
            profileStage !== 'EXITED';

        const isStatusContainer = is_accepted && !is_re_referred && 
            caseloadStatus === 'pending' && !referral.referee?.id;

        const shouldShowButtons = (
            (statusText === 'Pending' &&
                ((orgID === user.partner?.id && referrer.id !== user.id) ||
                    referral.referee?.id === user.id)) ||
            isStatusContainer
        );

        return {
            id: referral.id,
            type: 'case_management',
            created_at: referral.created_at,
            note: referral.note,
            statusText,
            referrer: {
                type: 'user',
                name: this.formatParticipantName(referral.referrer, user.id),
                subtext: referral.referrer.organization_name
            },
            referee: referral.referee ? {
                type: 'user',
                name: this.formatParticipantName(referral.referee, user.id),
                subtext: referral.referee.organization_name
            } : organization ? {
                type: 'organization',
                name: organization.name
            } : null,
            shouldShowButtons,
            isStatusContainer,
            isActiveCaseload
        };
    }
} 